import { isObject } from 'lodash-es';
import { use, useMemo } from 'react';
import {
  allowOrgCreation,
  getModel,
  hasModule,
  hasOrganizationsModule,
  notificationsEnabled,
  hasStripe
} from '../utils/models';
import { getModelAndAttributeFromPath } from '../utils/models.js';
import {
  ModelContext,
  ModelContextValue
} from '../components/models/ModelProvider';
import { RhinoResolvedResource, RhinoResourceSpecifier } from '..';
import { useTranslation } from '../i18n';

/**
 * Memoize a model by name or with an existing model object
 *
 * @example
 *    const model = useModel('blog_post')
 *    const model = useModel(getModel('blog_post'))
 */
export const useModel = <T extends RhinoResourceSpecifier>(
  model: T
): RhinoResolvedResource<T> =>
  useMemo(
    () =>
      (isObject(model) ? model : getModel(model)) as RhinoResolvedResource<T>,
    [model]
  );

// Its currently ok to use this hook outside of a ModelProvider
// FIXME: Require a ModelProvider context
export const useModelContext = (): ModelContextValue => {
  const context = use(ModelContext) ?? {};

  return context;
};

/**
 * Return whether a module is enabled
 *
 * @example
 *    const enabled = useHasModule('rhino')
 */
export const useHasModule = (module: string): boolean =>
  useMemo(() => hasModule(module), [module]);

/**
 * Whether organizations module is enabled
 *
 * @example
 *    const enabled = useHasOrganizationsModule()
 */
export const useHasOrganizationsModule = () =>
  useMemo(() => hasOrganizationsModule(), []);

/**
 * Whether in-app notifications are enabled
 *
 * @example
 *    const enabled = useNotificationsEnabled()
 */
export const useNotificationsEnabled = () =>
  useMemo(() => notificationsEnabled(), []);

/**
 * Whether subscriptions module is enabled
 *
 * @example
 *    const enabled = useHasSubscriptionsModule()
 */
export const useHasSubscriptionsModule = () =>
  useHasModule('rhino_subscriptions');

/**
 * Whether organization creation is allowed
 *
 * @example
 *    const allowed = useAllowOrgCreation()
 */
export const useAllowOrgCreation = () => useMemo(() => allowOrgCreation(), []);

/**
 * Whether Stripe billing is configured (optional backing).
 *
 * @example
 *    const stripeEnabled = useStripeEnabled()
 */
export const useStripeEnabled = () => useMemo(() => hasStripe(), []);

export const useModelAndAttributeFromPath = (
  model: RhinoResourceSpecifier,
  path: string
) => {
  const memoModel = useModel(model);
  const { t } = useTranslation();

  return useMemo(() => {
    const [model, attribute] = getModelAndAttributeFromPath(memoModel, path);

    return {
      model,
      attribute:
        attribute && model
          ? {
              ...attribute,
              enumLabels: Object.fromEntries(
                (attribute.enum ?? []).map((value) => [
                  String(value),
                  t(
                    `rhino.generated.${model.model}.enums.${attribute.name}.${value}`,
                    {
                      defaultValue: ''
                    }
                  )
                ])
              ),
              readableName: t(
                `rhino.generated.${model.model}.attributes.${attribute.name}`,
                { defaultValue: attribute.readableName }
              )
            }
          : attribute
    };
  }, [memoModel, path, t]);
};
